import { SubscriberPackageKey } from 'library/ts/model/user/SubscriberPackageKey';
import { SubscriberPackageValue } from 'library/ts/model/user/SubscriberPackageValue';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { IUserModelArgument } from 'tag/ts/tag/interfaces/arguments/IUserModelArgument';
import { ISubscriberStatus } from 'tag/ts/tag/interfaces/ISubscriberStatus';
import { IParamsUser } from 'tag/ts/tag/interfaces/params/IParamsUser';

/**
 * @copyright © Paramount Streaming
 * @description Model for User data from IParamsUser
 */
export class UserModel extends AbstractTagClass {

	constructor( argument: IUserModelArgument ) {
		super(
			{
				...argument,
				name: 'UserModel'
			}
		);

		this.#validate( argument?.paramsUser );

		this.#initSubscriberStatuses( argument?.paramsUser?.userStatus );
		this.#initBundleStatuses( argument?.paramsUser?.bundleStatus );
		this.#userId = argument?.paramsUser?.userId;
		this.#pinGate = argument?.paramsUser?.pinGate;
		this.#mvpdId = argument?.paramsUser?.mvpdId;
	}

	/**
	 * @description All valid subscriber status fields in userStatus. All other fields should be ignored.
	 */
	#validSubscriberKeys = {
		esb: false,
		ssb: false,
		sb: true,
		tsb: true,
		tve: true
	};

	#subscriberStatuses: ISubscriberStatus[];
	/**
	 * @description Initialize subscriberStatuses
	 */
	#initSubscriberStatuses = ( userStatusStr: string ): void => {
		// user has no subscriber statuses
		if ( this.utils.Type.isEmptyString( userStatusStr ) || !this.utils.Type.isString( userStatusStr ) ) {
			this.#subscriberStatuses = [];
			return;
		}

		this.#subscriberStatuses = userStatusStr.split( ',' )
			.map( ( status ) => status.trim() )
			.map( ( status ) => {
				// ex: "sb|14"

				if ( status === SubscriberPackageKey.ANON ) {
					// if "anon", then status is "sb|0"
					return <ISubscriberStatus>{
						key: SubscriberPackageKey.SB,
						value: SubscriberPackageValue.VALUE_0
					};
				}

				if ( status !== SubscriberPackageKey.ANON ) {
					const sections = status.split( '|' ); // ex: ["sb", "14"]
					return <ISubscriberStatus>{
						key: sections[ 0 ].trim().toLowerCase(), // ex: "sb",
						value: sections[ 1 ].trim() // ex: "14"
					};
				}
			} );
	};

	/**
	 * @description All user's subscriber statuses
	 */
	get subscriberStatuses() {
		return this.#subscriberStatuses;
	}

	/**
	 * @description Returns all valid subscriber statuses
	 * @return {ISubscriberStatus[]}
	 */
	getValidStatuses = (): ISubscriberStatus[] => this.subscriberStatuses.filter( ( status ) => this.#validSubscriberKeys[ status.key ] === true );
	/**
	 * @description Returns all invalid/deprecated subscriber statuses
	 * @return {ISubscriberStatus[]}
	 */
	getInvalidStatuses = (): ISubscriberStatus[] => this.subscriberStatuses.filter( ( status ) => this.#validSubscriberKeys[ status.key ] === false );
	/**
	 * @description Returns the current subscriber status
	 * @return {ISubscriberStatus}
	 */
	getCurrentValidStatus = (): ISubscriberStatus => this.getValidStatuses()[ 0 ];

	#bundleStatuses: string[];
	/**
	 * @description Initialize bundleStatuses
	 */
	#initBundleStatuses = ( bundleStatusStr: string ): void => {
		// user has no bundles
		if ( this.utils.Type.isEmpty( bundleStatusStr ) || !this.utils.Type.isString( bundleStatusStr ) ) {
			this.#bundleStatuses = [];
			return;
		}

		this.#bundleStatuses = bundleStatusStr.split( ',' ).map( ( status ) => status.trim() );
	};

	/**
	 * @description All user's bundles
	 * @example ["SHO", "BET"]
	 */
	get bundleStatuses() {
		return this.#bundleStatuses;
	}

	readonly #userId: string;
	/**
	 * @description User id passed from AA pages
	 */
	get userId() {
		return this.#userId;
	}

	readonly #pinGate: boolean;
	/**
	 * @description Flag enabling PIN Gate
	 */
	get pinGate() {
		return this.#pinGate;
	}

	readonly #mvpdId: string;
	/**
	 * @description MVPD ID
	 */
	get mvpdId() {
		return this.#mvpdId;
	}

	/**
	 * @description Validate parameters. If invalid, throws Error.
	 */
	#validate = ( params: IParamsUser ): void => {
		if ( this.utils.Type.isEmpty( params ) ) throw new Error( 'params are missing/invalid.' );
		if ( this.utils.Type.isEmpty( params.userStatus ) || !this.utils.Type.isString( params.userStatus ) ) throw new Error( 'userStatus is missing/invalid.' );
	};

}